'use client';

import { useParams, useRouter } from 'next/navigation';
import { useCollection } from '@/hooks/useCollections';
import Image from 'next/image';
import Link from 'next/link';
import { ArrowLeft, ShoppingCart, ImageIcon, Sparkles } from 'lucide-react';
import { Product, ProductInCatalog } from '@/types/products';
import { useLanguage } from '@/context/LanguageContext';
import { useTranslation } from '@/translations';
import { ProductCard } from '@/app/shop/components/ProductCard';
import { useShop } from '@/context/ShopContext';

export default function CollectionPageClient() {
  const { language } = useLanguage();
  const t = useTranslation(language);
  const params = useParams();
  const router = useRouter();
  const collectionId = params.id as string;
  const { addToWishlist, removeFromWishlist, isInWishlist } = useShop();

  const { data: collection, isLoading, error } = useCollection(collectionId);

  // Convert Product to ProductInCatalog for ProductCard
  const convertToProductInCatalog = (product: Product): ProductInCatalog => ({
    productId: product.productId,
    name: product.name,
    slug: product.slug,
    price: product.price,
    category: product.category,
    tags: product.tags,
    isFeatured: product.isFeatured,
    isNew: product.isNew,
    rating: product.rating || 0,
    reviewCount: product.reviewCount,
    available: product.available,
    images: product.images,
    flowerDetails: product.flowerDetails,
    stock: product.stock,
  });

  const handleToggleWishlist = (productId: string, name: string, price: number, images: string[]) => {
    if (isInWishlist(productId)) {
      removeFromWishlist(productId);
    } else {
      addToWishlist({
        productId,
        name,
        price,
        images,
      });
    }
  };

  if (isLoading) {
    return (
      <div className="organic min-h-screen flex items-center justify-center">
        <div
          className="animate-spin rounded-full h-12 w-12"
          style={{ borderTop: '2px solid var(--color-accent)', borderBottom: '2px solid var(--color-accent)' }}
        />
      </div>
    );
  }

  if (error || !collection) {
    return (
      <div className="organic min-h-screen flex items-center justify-center px-4">
        <div className="text-center">
          <h1 style={{ margin: '0 0 var(--space-3)' }}>{t('collection.collectionNotFound')}</h1>
          <p style={{ color: 'color-mix(in srgb, var(--color-text) 68%, transparent)', margin: '0 0 var(--space-4)' }}>
            {t('collection.collectionNotFoundText')}
          </p>
          <Link href="/collections" className="btn btn-primary">
            <ArrowLeft className="h-4 w-4" strokeWidth={2.75} />
            {t('collection.backToCollections')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <main className="organic min-h-screen">
      {/* Back link */}
      <div style={{ paddingTop: 64, borderBottom: '1px solid var(--color-divider)' }}>
        <div className="max-w-7xl mx-auto" style={{ padding: 'var(--space-3) clamp(20px, 4vw, 48px)' }}>
          <button onClick={() => router.push('/collections')} className="btn btn-ghost">
            <ArrowLeft className="h-4 w-4" strokeWidth={2.75} />
            {t('collection.backToCollections')}
          </button>
        </div>
      </div>

      {/* Collection header */}
      <div style={{ borderBottom: '1px solid var(--color-divider)' }}>
        <div
          className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 items-center"
          style={{ gap: 'var(--space-8)', padding: 'var(--space-8) clamp(20px, 4vw, 48px)' }}
        >
          <div className="relative" style={{ height: 384, borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
            {collection.image ? (
              <Image
                src={collection.image}
                alt={collection.name}
                fill
                className="washed object-cover"
                priority
              />
            ) : (
              <div className="flex items-center justify-center h-full" style={{ background: 'var(--color-neutral-100)' }}>
                <ImageIcon className="h-24 w-24" style={{ color: 'var(--color-neutral-500)' }} />
              </div>
            )}
            {collection.featured && (
              <span
                className="tag tag-accent absolute flex items-center gap-1"
                style={{ top: 12, right: 12 }}
              >
                <Sparkles className="h-2.5 w-2.5" strokeWidth={2.75} />
                {t('collection.featured')}
              </span>
            )}
          </div>

          <div>
            <h1 style={{ margin: '0 0 var(--space-3)', fontSize: 'clamp(28px, 3.4vw, 42px)' }}>
              {collection.name}
            </h1>
            <p
              style={{
                fontSize: 16.5, lineHeight: 1.6,
                color: 'color-mix(in srgb, var(--color-text) 78%, transparent)',
                margin: '0 0 var(--space-4)',
              }}
            >
              {collection.description}
            </p>
            <span
              className="inline-flex items-center"
              style={{ gap: 6, fontSize: 13.5, color: 'color-mix(in srgb, var(--color-text) 65%, transparent)' }}
            >
              <ShoppingCart className="h-4 w-4" strokeWidth={2.75} />
              {collection.products?.length || 0} {t('collection.products')}
            </span>
          </div>
        </div>
      </div>

      {/* Products grid */}
      <div className="max-w-7xl mx-auto" style={{ padding: 'var(--space-8) clamp(20px, 4vw, 48px)' }}>
        <h2 style={{ margin: '0 0 var(--space-6)', fontSize: 26 }}>
          {t('collection.productsInCollection')}
        </h2>

        {collection.productsDetails && collection.productsDetails.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {collection.productsDetails.map((product: Product) => (
              <ProductCard
                key={product.productId}
                product={convertToProductInCatalog(product)}
                onToggleWishlist={handleToggleWishlist}
              />
            ))}
          </div>
        ) : (
          <div className="card elev-sm text-center" style={{ padding: 'calc(var(--space-8) * 1.2)' }}>
            <ImageIcon className="mx-auto h-14 w-14" style={{ color: 'var(--color-neutral-500)', marginBottom: 'var(--space-3)' }} />
            <h3 style={{ margin: '0 0 var(--space-2)' }}>{t('collection.noProductsYet')}</h3>
            <p style={{ color: 'color-mix(in srgb, var(--color-text) 68%, transparent)', margin: '0 0 var(--space-4)' }}>
              {t('collection.noProductsText')}
            </p>
            <Link href="/shop" className="btn btn-primary">
              {t('collection.browseAll')}
            </Link>
          </div>
        )}
      </div>

      {/* Closing CTA band */}
      <section style={{ background: 'var(--color-neutral-100)', padding: 'calc(var(--space-8) * 1.4) clamp(20px, 4vw, 48px)' }}>
        <div className="mx-auto text-center" style={{ maxWidth: 720 }}>
          <h2 style={{ fontSize: 28, margin: '0 0 var(--space-2)' }}>{t('collection.exploreMore')}</h2>
          <p style={{ color: 'color-mix(in srgb, var(--color-text) 70%, transparent)', margin: '0 0 var(--space-4)' }}>
            {t('collection.exploreMoreText')}
          </p>
          <Link href="/collections" className="btn btn-primary">
            {t('collection.viewAllCollections')}
          </Link>
        </div>
      </section>
    </main>
  );
}
